import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { datadogRum } from '@datadog/browser-rum';
import { identify } from '@fullstory/browser';

import { CurrentUser } from '@imaging/types';
import { UnauthorizedError } from '@/core/http/errors';
import { Environment } from '@/core/utils';
import { userKeyFactory } from '@/core/query-keys';
import { AuthGateway } from '../gateways';

interface FullStoryUser {
  id: string;
  userId: number;
  databaseId: number;
  userName: string;
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const response = await AuthGateway.getCurrentUser();

  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  const message = await response.text().catch(() => 'Request failed');
  datadogRum.addError(message);
  throw new Error(message);
}

export function handleCurrentUserError(error: unknown) {
  if (error instanceof UnauthorizedError) {
    window.location.href = process.env.NEXT_PUBLIC_ACCOUNTS_URL as string;
  }
}

function setFullStoryUser({ id, userId, databaseId, userName }: FullStoryUser) {
  if (Environment.isDeployed) {
    identify(id, {
      userId,
      databaseId,
      userName
    });
  }
}

export function useCurrentUser(options: UseQueryOptions<CurrentUser> = {}) {
  return useQuery<CurrentUser>(userKeyFactory.currentUser, fetchCurrentUser, {
    onError: handleCurrentUserError,
    onSuccess: ({ id, userId, databaseId, userName }) => {
      datadogRum.setUser({
        userId: userId.toString(),
        databaseId,
        username: userName
      });

      setFullStoryUser({ id, userId, databaseId, userName });
    },
    ...options
  });
}
